import { logger } from '@/services/logger';
import { SanitizedSecretDto, SecretAuthType, TriggerWebhook, WebhookAuth, WebhookSecretConfig } from '@d-id/types';
import { WebhookFormValues } from '../types';

const SECRET_MASK = '*********';

export const isNewFormat = (auth: WebhookAuth | undefined): boolean => {
    if (!auth) return false;
    return !!auth.secret_id && !auth.type;
};

export const isOldFormat = (auth: WebhookAuth | undefined): boolean => {
    if (!auth) return false;
    return !!auth.type;
};

export const parseSecretConfig = (configString?: string): WebhookSecretConfig => {
    if (!configString) return {};
    try {
        return JSON.parse(configString) as WebhookSecretConfig;
    } catch (e) {
        logger.error('Failed to parse secret config', { error: e });
        return {};
    }
};

export const parseFormDataToTriggerWebhook = (data: WebhookFormValues): TriggerWebhook => {
    const url = new URL(data.url);
    data.queryParams?.forEach(({ name, value }) => {
        if (name && value) {
            url.searchParams.append(name, value);
        }
    });

    const headers = data.headers?.reduce(
        (acc, { name, value }) => {
            if (name && value) {
                acc[name] = value;
            }
            return acc;
        },
        {} as Record<string, string>
    );

    const filteredAuth = data.auth
        ? Object.fromEntries(
              Object.entries(data.auth).filter(([_, value]) => {
                  return value !== SECRET_MASK && !!value;
              })
          )
        : data.auth;

    return {
        url: url.toString(),
        headers,
        auth: filteredAuth as WebhookAuth,
    };
};

export const parseTriggerWebhookToFormData = (triggerWebhook: TriggerWebhook): WebhookFormValues => {
    const url = new URL(triggerWebhook.url);
    const headers = Object.entries(triggerWebhook.headers || {}).map(([name, value]) => ({ name, value }));
    const queryParams = Array.from(url.searchParams.entries()).map(([name, value]) => ({ name, value }));

    const auth = () => {
        if (triggerWebhook.auth?.type === 'basic') {
            return {
                type: 'basic',
                username: triggerWebhook.auth?.username,
                password: triggerWebhook.auth?.password || SECRET_MASK,
                secret_id: triggerWebhook.auth?.secret_id,
            };
        }

        if (triggerWebhook.auth?.type === 'api_key') {
            return {
                type: 'api_key',
                header_name: triggerWebhook.auth?.header_name,
                api_key: triggerWebhook.auth?.api_key || SECRET_MASK,
                secret_id: triggerWebhook.auth?.secret_id,
            };
        }

        if (triggerWebhook.auth?.type === 'bearer') {
            return {
                type: 'bearer',
                token: triggerWebhook.auth?.token || SECRET_MASK,
                secret_id: triggerWebhook.auth?.secret_id,
            };
        }

        return triggerWebhook.auth;
    };

    return {
        url: url.href.split('?')[0],
        headers,
        queryParams,
        auth: auth() as WebhookAuth,
    };
};

export const parseTriggerWebhookToFormDataNew = (triggerWebhook: TriggerWebhook, secret: SanitizedSecretDto): WebhookFormValues => {
    const url = new URL(triggerWebhook.url);
    const headers = Object.entries(triggerWebhook.headers || {}).map(([name, value]) => ({ name, value }));
    const queryParams = Array.from(url.searchParams.entries()).map(([name, value]) => ({ name, value }));

    const config = parseSecretConfig(secret.config);
    const baseAuth = {
        secret_id: secret.id,
    };

    let auth: WebhookAuth;

    switch (secret.type) {
        case SecretAuthType.Bearer:
            auth = {
                ...baseAuth,
                type: 'bearer',
                token: secret.sanitized_secret, // masked value
                ...(config.header_name && { header_name: config.header_name }),
            };
            break;
        case SecretAuthType.ApiKey:
            auth = {
                ...baseAuth,
                type: 'api_key',
                api_key: secret.sanitized_secret, // masked value
                ...(config.header_name && { header_name: config.header_name }),
            };
            break;
        case SecretAuthType.Basic:
            auth = {
                ...baseAuth,
                type: 'basic',
                username: config.username,
                password: secret.sanitized_secret, // masked value
                ...(config.header_name && { header_name: config.header_name }),
            };
            break;
        default:
            auth = baseAuth as WebhookAuth;
    }

    return {
        url: url.href.split('?')[0],
        headers,
        queryParams,
        auth,
    };
};
